import { useState, useRef, useEffect } from 'react';
import { API_BASE } from '../config';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';

const C = {
    white:    '#ffffff',
    ink:      '#111111',
    red:      '#D0202A',
    redSoft:  '#FDEAEA',
    green:    '#158A45',
    greenSoft:'#EAFAF0',
    gray:     '#6b6b6b',
    line:     '#e6e6e6',
    panel:    '#f7f7f7',
};

const MAX_IMAGE_WIDTH = 1280;

function compressImage(file) {
    return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                let { width, height } = img;
                if (width > MAX_IMAGE_WIDTH) {
                    height = Math.round((height * MAX_IMAGE_WIDTH) / width);
                    width  = MAX_IMAGE_WIDTH;
                }
                canvas.width  = width;
                canvas.height = height;
                canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.78);
            };
            img.src = e.target.result;
        };
        reader.readAsDataURL(file);
    });
}

export default function PoBoxImages({ user, onBack }) {
    const [poNumber,     setPoNumber]     = useState('');
    const [pendingImgs,  setPendingImgs]  = useState([]); // [{file, preview}] — not yet uploaded
    const [uploadedImgs, setUploadedImgs] = useState([]); // [{name}] — successfully uploaded
    const [uploading,    setUploading]    = useState(false);
    const [showConfirm,  setShowConfirm]  = useState(false);
    const [toast,        setToast]        = useState('');
    const [error,        setError]        = useState('');
    // ── In-page continuous camera (replaces OS camera hand-off + confirm tap) ──
    const [cameraOpen,   setCameraOpen]   = useState(false);

    const fileInputRef = useRef(null);
    const videoRef      = useRef(null);
    const streamRef     = useRef(null);

    // Release the camera if the component unmounts while the live preview is open.
    useEffect(() => {
        return () => { streamRef.current?.getTracks().forEach(t => t.stop()); };
    }, []);

    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(''), 3000);
    };

    // ── Add images to pending list (no upload yet) ────────────────────────
    const handleImageCapture = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        const compressed = await Promise.all(
            files.map(async (f) => ({
                file:    await compressImage(f),
                preview: URL.createObjectURL(f),
            }))
        );
        setPendingImgs((prev) => [...prev, ...compressed]);
        e.target.value = '';
    };

    // Live in-page camera — tap once to open, tap the capture button repeatedly
    // for continuous point-and-shoot (no OS camera hand-off, no confirm tap).
    // Falls back to the existing OS-camera file input on any device/browser
    // that doesn't support getUserMedia (or where the user denies permission).
    async function openCamera() {
        if (!poNumber.trim()) { setError('Enter a PO number first'); return; }
        if (!navigator.mediaDevices?.getUserMedia) {
            fileInputRef.current?.click();
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode: { ideal: 'environment' },
                    width:      { ideal: 3840 },
                    height:     { ideal: 2160 }
                },
                audio: false
            });
            streamRef.current = stream;
            setCameraOpen(true);
        } catch (err) {
            console.error('getUserMedia failed, falling back to OS camera:', err);
            fileInputRef.current?.click();
        }
    }

    function closeCamera() {
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        setCameraOpen(false);
    }

    // Callback ref — fires the instant the <video> element mounts, so the
    // live stream attaches with no race against React's render timing.
    function attachVideoRef(el) {
        videoRef.current = el;
        if (el && streamRef.current && el.srcObject !== streamRef.current) {
            el.srcObject = streamRef.current;
            el.play().catch(err => console.error('Video play() failed:', err));
        }
    }

    // Captures the current frame, runs it through the same compressImage()
    // pipeline the OS-camera path already uses, so downstream behavior
    // (thumbnail preview, upload size) is identical either way.
    function captureFrame() {
        const video = videoRef.current;
        if (!video || !video.videoWidth) return;
        const canvas = document.createElement('canvas');
        canvas.width  = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(async (blob) => {
            if (!blob) return;
            const file = new File([blob], `capture_${Date.now()}.jpg`, { type: 'image/jpeg' });
            const compressed = await compressImage(file);
            setPendingImgs((prev) => [...prev, { file: compressed, preview: URL.createObjectURL(blob) }]);
        }, 'image/jpeg', 0.95);
    }

    const removeImage = (idx) =>
        setPendingImgs((prev) => prev.filter((_, i) => i !== idx));

    // ── Confirm popup → actual upload ─────────────────────────────────────
    const handleUpload = async () => {
        if (!poNumber.trim() || pendingImgs.length === 0) return;
        setShowConfirm(false);
        setUploading(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('po_number',   poNumber.trim());
            formData.append('uploaded_by', user?.display_name || user?.username || '');

            for (let i = 0; i < pendingImgs.length; i++) {
                const imgNumber = uploadedImgs.length + i + 1;
                const blobName  = `${poNumber.trim()}_img_${imgNumber}.jpg`;
                formData.append('images', pendingImgs[i].file, blobName);
            }

            const res  = await fetch(`${API_BASE}/wh-po-images-upload`, {
                method: 'POST',
                body:   formData,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error || 'Upload failed');

            // Move pending → uploaded
            const newUploaded = pendingImgs.map((_, i) => ({
                name: `${poNumber.trim()}_img_${uploadedImgs.length + i + 1}.jpg`,
            }));
            setUploadedImgs((prev) => [...prev, ...newUploaded]);
            setPendingImgs([]);
            showToast(`✓ ${newUploaded.length} photo${newUploaded.length > 1 ? 's' : ''} saved`);

        } catch (err) {
            setError(err.message);
        }
        setUploading(false);
    };

    // ── LIVE BOX CAMERA — continuous point-and-shoot, no OS hand-off ────────
    if (cameraOpen) return (
        <div style={{ position:'fixed', inset:0, background:'#000', zIndex:300, display:'flex', flexDirection:'column', overflow:'hidden' }}>
            <video ref={attachVideoRef} autoPlay playsInline muted
                style={{ flex:'1 1 0', minHeight:0, minWidth:0, width:'100%', objectFit:'contain', background:'#000' }} />
            <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:'28px', padding:'20px 16px', background:'#111' }}>
                <button onClick={closeCamera}
                    style={{ background:'#444', color:'#fff', border:'none', borderRadius:'10px', padding:'12px 20px', fontWeight:700, fontSize:'14px', cursor:'pointer' }}>
                    Done
                </button>
                <button onClick={captureFrame}
                    style={{ width:'68px', height:'68px', borderRadius:'50%', background:C.red, border:'3px solid #fff', cursor:'pointer' }} />
                <div style={{ width:'68px', textAlign:'center', color:'#fff', fontSize:'12px', fontWeight:700 }}>
                    {pendingImgs.length} staged
                </div>
            </div>
        </div>
    );

    const S = {
        page:    { minHeight:'100vh', background:C.white, color:C.ink, fontFamily:'-apple-system, BlinkMacSystemFont, sans-serif', maxWidth:'430px', margin:'0 auto' },
        header:  { background:C.ink, padding:'16px 18px', position:'sticky', top:0, zIndex:100 },
        body:    { padding:'16px' },
        input:   { width:'100%', padding:'14px 12px', background:'#f9f9f9', border:`1px solid ${C.line}`, borderRadius:'8px', color:'#111', fontSize:'17px', outline:'none', boxSizing:'border-box', fontFamily:'monospace', letterSpacing:'0.5px' },
        priBtn:  { width:'100%', padding:'16px', background:C.ink, color:'#fff', border:'none', borderRadius:10, fontSize:16, fontWeight:700, cursor:'pointer' },
        overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:300, display:'flex', alignItems:'flex-end', justifyContent:'center' },
        modal:   { background:C.white, borderRadius:'16px 16px 0 0', width:'100%', maxWidth:'430px', padding:'24px 20px', border:`2px solid ${C.ink}`, borderBottom:'none' },
    };

    return (
        <div style={S.page}>

            {/* Toast */}
            {toast && (
                <div style={{ position:'fixed', top:14, left:'50%', transform:'translateX(-50%)', background:C.ink, color:'#fff', padding:'10px 20px', borderRadius:20, fontSize:13, zIndex:9999, whiteSpace:'nowrap', boxShadow:'0 4px 12px rgba(0,0,0,0.25)' }}>
                    {toast}
                </div>
            )}

            {/* Confirmation popup */}
            {showConfirm && (
                <div style={S.overlay}>
                    <div style={S.modal}>
                        <div style={{ fontSize:'22px', textAlign:'center', marginBottom:8 }}>📦</div>
                        <div style={{ fontSize:'17px', fontWeight:800, textAlign:'center', marginBottom:6 }}>Confirm Upload</div>
                        <div style={{ fontSize:'13px', color:C.gray, textAlign:'center', marginBottom:18 }}>
                            Please check the details before saving
                        </div>

                        {/* Summary */}
                        <div style={{ background:C.panel, borderRadius:10, padding:'14px', marginBottom:18 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                                <span style={{ fontSize:12, color:C.gray }}>PO Number</span>
                                <span style={{ fontSize:13, fontWeight:700, fontFamily:'monospace' }}>{poNumber.trim()}</span>
                            </div>
                            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                                <span style={{ fontSize:12, color:C.gray }}>Photos to upload</span>
                                <span style={{ fontSize:13, fontWeight:700 }}>{pendingImgs.length}</span>
                            </div>
                            <div style={{ display:'flex', justifyContent:'space-between' }}>
                                <span style={{ fontSize:12, color:C.gray }}>Will be named</span>
                                <span style={{ fontSize:11, fontFamily:'monospace', color:C.ink }}>
                                    {poNumber.trim()}_img_{uploadedImgs.length + 1}
                                    {pendingImgs.length > 1 ? ` … _img_${uploadedImgs.length + pendingImgs.length}` : ''}
                                </span>
                            </div>
                        </div>

                        {/* Thumbnail preview strip */}
                        <div style={{ display:'flex', gap:6, overflowX:'auto', marginBottom:20, paddingBottom:4 }}>
                            {pendingImgs.map((img, i) => (
                                <img key={i} src={img.preview} alt="" style={{ width:56, height:56, objectFit:'cover', borderRadius:6, flexShrink:0, border:`1.5px solid ${C.line}` }} />
                            ))}
                        </div>

                        <div style={{ display:'flex', gap:10 }}>
                            <button
                                onClick={() => setShowConfirm(false)}
                                style={{ flex:1, padding:'13px', background:C.panel, border:`1px solid ${C.line}`, borderRadius:8, fontSize:14, fontWeight:600, cursor:'pointer', color:C.ink }}
                            >
                                ← Go Back
                            </button>
                            <button
                                onClick={() => { if (!hasEditAccess(user, 'warehouse', 'warehouse_access')) return notifyEditBlocked(); handleUpload(); }}
                                style={{ flex:2, padding:'13px', background:C.green, color:'#fff', border:'none', borderRadius:8, fontSize:15, fontWeight:700, cursor:'pointer' }}
                            >
                                ✓ Save Photos
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header */}
            <div style={S.header}>
                <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                    <button onClick={onBack} style={{ background:'none', border:'none', color:'#fff', fontSize:'22px', cursor:'pointer', padding:0, lineHeight:1 }}>←</button>
                    <div>
                        <div style={{ fontSize:'10px', color:C.red, letterSpacing:'2px', textTransform:'uppercase', fontWeight:800 }}>Soulflower</div>
                        <div style={{ fontSize:'19px', fontWeight:800, color:'#fff' }}>Box Images</div>
                    </div>
                </div>
            </div>

            <div style={S.body}>

                {/* PO Number input */}
                <div style={{ marginBottom:20 }}>
                    <div style={{ fontSize:'11px', color:C.gray, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px' }}>
                        PO Number
                    </div>
                    <input
                        type="text"
                        placeholder="e.g. BLK_MP_204596199"
                        value={poNumber}
                        onChange={(e) => { setPoNumber(e.target.value); setError(''); }}
                        style={S.input}
                        autoComplete="off"
                        autoCapitalize="none"
                    />
                </div>

                {error && (
                    <div style={{ background:'#fef2f2', border:`1px solid #fca5a5`, borderRadius:8, padding:'10px 12px', color:'#dc2626', fontSize:13, marginBottom:16 }}>
                        {error}
                    </div>
                )}

                {/* Image grid — pending images + add more button */}
                {pendingImgs.length > 0 && (
                    <div style={{ marginBottom:20 }}>
                        <div style={{ fontSize:'11px', color:C.gray, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:10 }}>
                            Photos to upload ({pendingImgs.length})
                        </div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>

                            {/* + Add more tile — leftmost */}
                            <button onClick={openCamera}
                                style={{ width:80, height:80, background:C.panel, border:`2px dashed ${C.ink}`, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:28, cursor:'pointer', flexShrink:0, color:C.ink }}>
                                +
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                capture="environment"
                                multiple
                                onChange={handleImageCapture}
                                style={{ display:'none' }}
                            />

                            {/* Pending image thumbnails */}
                            {pendingImgs.map((img, i) => (
                                <div key={i} style={{ position:'relative', flexShrink:0 }}>
                                    <img
                                        src={img.preview}
                                        alt=""
                                        style={{ width:80, height:80, objectFit:'cover', borderRadius:8, border:`2px solid ${C.line}`, display:'block' }}
                                    />
                                    {/* Cross to remove */}
                                    <div
                                        onClick={() => removeImage(i)}
                                        style={{ position:'absolute', top:-7, right:-7, background:C.red, color:'#fff', borderRadius:'50%', width:20, height:20, fontSize:11, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', fontWeight:700, boxShadow:'0 1px 4px rgba(0,0,0,0.3)' }}
                                    >
                                        ✕
                                    </div>
                                    {/* Image number label */}
                                    <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'rgba(0,0,0,0.5)', borderRadius:'0 0 6px 6px', padding:'2px 0', textAlign:'center', fontSize:9, color:'#fff', fontWeight:700 }}>
                                        img_{uploadedImgs.length + i + 1}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* First camera button — shown when no pending images yet */}
                {pendingImgs.length === 0 && (
                    <>
                        <button onClick={openCamera} disabled={!poNumber.trim()} style={{
                            display:      'block',
                            width:        '100%',
                            padding:      '18px',
                            background:   poNumber.trim() ? C.ink : '#ccc',
                            border:       'none',
                            borderRadius: 10,
                            fontSize:     16,
                            fontWeight:   700,
                            cursor:       poNumber.trim() ? 'pointer' : 'not-allowed',
                            textAlign:    'center',
                            color:        '#fff',
                            boxSizing:    'border-box',
                            marginBottom: 16,
                        }}>
                            📷 Capture Box Photo
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            multiple
                            onChange={handleImageCapture}
                            disabled={!poNumber.trim()}
                            style={{ display:'none' }}
                        />
                    </>
                )}

                {/* Upload all button */}
                {pendingImgs.length > 0 && (
                    <button
                        style={{ ...S.priBtn, background: uploading ? '#888' : C.ink }}
                        onClick={() => {
                            if (!poNumber.trim()) { setError('Enter a PO number first'); return; }
                            setShowConfirm(true);
                        }}
                        disabled={uploading}
                    >
                        {uploading ? '⏳ Uploading…' : `↑ Upload ${pendingImgs.length} Photo${pendingImgs.length > 1 ? 's' : ''}`}
                    </button>
                )}

                {/* Already uploaded this session */}
                {uploadedImgs.length > 0 && (
                    <div style={{ marginTop:20, padding:'12px 14px', background:C.greenSoft, border:`1px solid ${C.green}`, borderRadius:8, textAlign:'center' }}>
                        <div style={{ fontSize:13, fontWeight:700, color:C.green }}>
                            ✓ {uploadedImgs.length} photo{uploadedImgs.length > 1 ? 's' : ''} saved to po-box-images
                        </div>
                        <div style={{ fontSize:11, color:C.gray, marginTop:3 }}>
                            {uploadedImgs.map(i => i.name).join(', ')}
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
}